import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
// import { GENRE_LIST } from './mockdata';
import { CouponClassificationMap } from 'features/common/models';
import { Genre as GenreDetail } from 'features/Genre/models/Genre';
import { SubGenre as SubGenreDetail } from 'features/SubGenre/models/SubGenre';
import { FormSearchValueProps } from './components/MenuBookEditorModal';
import {
  ChildSubMenu,
  ChildSubMenuGroup,
  Genre,
  Menu,
  MenuBookEditorDeletePayload,
  MenuBookEditorGetAllPayload,
  SubGenre,
  SubMenu,
  SubMenuGroup,
} from './models';
import { MultiSelectToArgPayload } from './types';
import { multiSelectTo } from './utils';

export interface MenuBookEditorState {
  loading: boolean;
  expandAll?: boolean;
  formSearchValue?: FormSearchValueProps;
  couponClassificationSource: CouponClassificationMap;
  data: Genre[];
  selectedItemIds: number[];
  selectedDroppableId?: string;
  draggingItemId?: number;
  deletedSubGenreMenuIds: number[];
}

const initialState: MenuBookEditorState = {
  loading: false,
  expandAll: undefined,
  couponClassificationSource: {},
  data: [],
  selectedItemIds: [],
  deletedSubGenreMenuIds: [],
};

const menuBookEditorSlice = createSlice({
  name: 'menu_book_editor',
  initialState,
  reducers: {
    resetData(state) {
      state.loading = false;
      state.expandAll = undefined;
      state.data = [];
      state.selectedItemIds = [];
      state.deletedSubGenreMenuIds = [];
    },
    resetDeletedSubGenreMenuIds(state) {
      state.deletedSubGenreMenuIds = [];
    },
    setFormSearchValue(state, action: PayloadAction<FormSearchValueProps>) {
      state.formSearchValue = action.payload;
    },
    setCouponClassificationSource(state, action: PayloadAction<CouponClassificationMap>) {
      state.couponClassificationSource = action.payload;
    },
    fetchData(state, action: PayloadAction<MenuBookEditorGetAllPayload>) {
      state.loading = true;
      state.data = [];
    },
    fetchDataSuccess(state, action: PayloadAction<Genre[]>) {
      state.loading = false;
      state.data = action.payload;
    },
    fetchDataFailed(state) {
      state.loading = false;
    },
    unselectAll(state) {
      state.selectedItemIds = [];
      state.draggingItemId = undefined;
      state.selectedDroppableId = undefined;
    },
    setDraggingItemId(state, action: PayloadAction<number | undefined>) {
      state.draggingItemId = action.payload;
    },
    setExpandAll(state, action: PayloadAction<boolean | undefined>) {
      state.expandAll = action.payload;
    },
    updateState(state, action: PayloadAction<Partial<MenuBookEditorState>>) {
      state.data = action.payload.data || [];
      state.selectedItemIds = action.payload.selectedItemIds || [];
      state.draggingItemId = action.payload.draggingItemId;
    },
    toggleSelection(state, action: PayloadAction<{ selectedDroppableId: string; itemId: number }>) {
      const selectedItemIds = state.selectedItemIds;
      const wasSelected: boolean =
        state.selectedDroppableId === action.payload.selectedDroppableId &&
        selectedItemIds.includes(action.payload.itemId);

      const newItemIds: number[] = (() => {
        // Task was not previously selected
        // now will be the only selected item
        if (!wasSelected) {
          return [action.payload.itemId];
        }

        // Task was part of a selected group
        // will now become the only selected item
        if (selectedItemIds.length > 1) {
          return [action.payload.itemId];
        }

        // task was previously selected but not in a group
        // we will now clear the selection
        return [];
      })();

      state.selectedDroppableId = action.payload.selectedDroppableId;
      state.selectedItemIds = newItemIds;
    },

    toggleSelectionInGroup(state, action: PayloadAction<{ selectedDroppableId: string; itemId: number }>) {
      const selectedItemIds = state.selectedItemIds;
      const index: number = selectedItemIds.indexOf(action.payload.itemId);

      // if not selected - add it to the selected items
      if (index === -1) {
        state.selectedItemIds = [...selectedItemIds, action.payload.itemId];
        return;
      }

      // it was previously selected and now needs to be removed from the group
      const shallow = [...selectedItemIds];
      shallow.splice(index, 1);
      state.selectedItemIds = shallow;

      state.selectedDroppableId = action.payload.selectedDroppableId;
    },

    multiSelectTo(state, action: PayloadAction<{ arg: MultiSelectToArgPayload; newItemId: number }>) {
      const updated = multiSelectTo(
        {
          data: state.data as Genre[],
          selectedItemIds: state.selectedItemIds,
          type: action.payload.arg.type,
          droppableId: action.payload.arg.droppableId,
        },
        action.payload.newItemId
      );

      if (updated == null) {
        return;
      }
      state.selectedItemIds = updated;
    },
    addGenres(state, action: PayloadAction<Genre[]>) {
      state.data = [...state.data, ...action.payload];
    },
    addSubGenres(state, action: PayloadAction<{ genreId: number; subGenres: SubGenre[] }>) {
      state.data = state.data.map((genre) => {
        if (genre.id === action.payload.genreId) {
          return {
            ...genre,
            subgenres: [...genre.subgenres, ...action.payload.subGenres],
          };
        }
        return genre;
      });
    },
    addMenus(state, action: PayloadAction<{ genreId: number; subGenreId: number; menus: Menu[] }>) {
      const updatedGenre = (state.data as Genre[]).find((genre: Genre) => genre.id === action.payload.genreId);
      if (updatedGenre) {
        const updatedSubGenre = (updatedGenre.subgenres as SubGenre[]).find(
          (subGenre: SubGenre) => subGenre.id === action.payload.subGenreId
        );
        if (updatedSubGenre) {
          updatedSubGenre.menus = [...updatedSubGenre.menus, ...action.payload.menus];
        }
      }
    },
    delete(state, action: PayloadAction<MenuBookEditorDeletePayload>) {
      // delete menubookGenres
      action.payload.menubookGenres.forEach(({ genreId }) => {
        state.data = state.data.filter((genre: Genre) => genre.id !== genreId);
      });
      // delete courseGenres
      action.payload.courseGenres.forEach(({ genreId }) => {
        state.data = state.data.filter((genre: Genre) => genre.id !== genreId);
      });
      // delete subgenres
      action.payload.subgenres.forEach(({ genreId, subGenreId }) => {
        state.data = state.data.map((genre: Genre) => {
          if (genre.id === genreId) {
            return {
              ...genre,
              subgenres: genre.subgenres.filter((subGenre: SubGenre) => subGenre.id !== subGenreId),
            };
          }
          return genre;
        });
      });
      // delete menus
      action.payload.menus.forEach(({ genreId, subGenreId, menuId, subGenreMenuId }) => {
        if (subGenreMenuId > 0) {
          state.deletedSubGenreMenuIds.push(subGenreMenuId);
        }
        const targetGenre = (state.data as Genre[]).find((genre: Genre) => genre.id === genreId);
        if (targetGenre) {
          const targetSubGenre = (targetGenre.subgenres as SubGenre[]).find(
            (subGenre: SubGenre) => subGenre.id === subGenreId
          );
          if (targetSubGenre) {
            targetSubGenre.menus = targetSubGenre.menus.filter((menu: Menu) => menu.id !== menuId);
          }
        }
      });
    },
    updateGenre(state, action: PayloadAction<GenreDetail>) {
      const targetGenre = (state.data as Genre[]).find((genre: Genre) => genre.id === action.payload.id);
      if (targetGenre) {
        targetGenre.name = action.payload.name;
      }
    },
    updateSubGenre(state, action: PayloadAction<SubGenreDetail>) {
      (state.data as Genre[]).forEach((genre: Genre) => {
        (genre.subgenres as SubGenre[]).forEach((subGenre: SubGenre) => {
          if (subGenre.id === action.payload.id) {
            subGenre.name = action.payload.name;
          }
        });
      });
    },
    fetchSubMenuGroupData(state, action: PayloadAction<{ subMenuGroupId: number; dataDate: Date }>) {
      state.loading = true;
    },
    fetchSubMenuGroupDataSuccess(state, action: PayloadAction<{ subMenuGroupId: number; subMenus: SubMenu[] }>) {
      state.loading = false;

      //update submenus
      (state.data as Genre[]).forEach((genre: Genre) => {
        (genre.subgenres as SubGenre[]).forEach((subGenre: SubGenre) => {
          (subGenre.menus as Menu[]).forEach((menu: Menu) => {
            (menu.sub_menu_groups as SubMenuGroup[]).forEach((subMenuGroup: SubMenuGroup) => {
              if (subMenuGroup.id === action.payload.subMenuGroupId) {
                // filter active submenus
                const activeSubMenuIds = action.payload.subMenus.map((subMenu: SubMenu) => subMenu.id);
                subMenuGroup.sub_menus = subMenuGroup.sub_menus.filter((subMenu: SubMenu) =>
                  activeSubMenuIds.includes(subMenu.id)
                );
                //update submenus
                subMenuGroup.sub_menus.forEach((subMenu: SubMenu) => {
                  const updatedSubMenu = action.payload.subMenus.find(
                    (updatedSubMenu: SubMenu) => updatedSubMenu.id === subMenu.id
                  );
                  if (updatedSubMenu) {
                    subMenu.priority = updatedSubMenu.priority;
                  }
                });
              }
            });
          });
        });
      });

      //update child submenus
      (state.data as Genre[]).forEach((genre: Genre) => {
        (genre.subgenres as SubGenre[]).forEach((subGenre: SubGenre) => {
          (subGenre.menus as Menu[]).forEach((menu: Menu) => {
            (menu.sub_menu_groups as SubMenuGroup[]).forEach((subMenuGroup: SubMenuGroup) => {
              (subMenuGroup.sub_menus as SubMenu[]).forEach((subMenu: SubMenu) => {
                (subMenu.child_sub_menu_groups as ChildSubMenuGroup[]).forEach(
                  (childSubMenuGroup: ChildSubMenuGroup) => {
                    if (childSubMenuGroup.id === action.payload.subMenuGroupId) {
                      // filter active child submenus
                      const activeChildSubMenuIds = action.payload.subMenus.map(
                        (payloadSubMenu: SubMenu) => payloadSubMenu.id
                      );
                      childSubMenuGroup.child_sub_menus = childSubMenuGroup.child_sub_menus.filter(
                        (childSubMenu: ChildSubMenu) => activeChildSubMenuIds.includes(childSubMenu.id)
                      );
                      //update child submenus
                      childSubMenuGroup.child_sub_menus.forEach((childSubMenu: ChildSubMenu) => {
                        const updatedChildSubMenu = action.payload.subMenus.find(
                          (updatedSubMenu: SubMenu) => updatedSubMenu.id === childSubMenu.id
                        );
                        if (updatedChildSubMenu) {
                          childSubMenu.priority = updatedChildSubMenu.priority;
                        }
                      });
                    }
                  }
                );
              });
            });
          });
        });
      });
    },
    fetchSubMenuGroupDataFailed(state) {
      state.loading = false;
    },
  },
});

// Actions
export const menuBookEditorActions = menuBookEditorSlice.actions;

// Selectors
export const selectMenuBookEditorLoading = (state: RootState) => state.menuBookEditor.loading;
export const selectMenuBookEditorExpandAll = (state: RootState) => state.menuBookEditor.expandAll;
export const selectMenuBookEditorSelectedDroppableId = (state: RootState) => state.menuBookEditor.selectedDroppableId;
export const selectMenuBookEditorSelectedItemIds = (state: RootState) => state.menuBookEditor.selectedItemIds;
export const selectMenuBookEditorDraggingItemId = (state: RootState) => state.menuBookEditor.draggingItemId;
export const selectMenuBookEditorData = (state: RootState) => state.menuBookEditor.data;
export const selectMenuBookEditorDeletedSubGenreMenuIds = (state: RootState) =>
  state.menuBookEditor.deletedSubGenreMenuIds;
export const selectMenuBookEditorCouponClassificationSource = (state: RootState) =>
  state.menuBookEditor.couponClassificationSource;
export const selectMenuBookEditorFormSearchValue = (state: RootState) => state.menuBookEditor.formSearchValue;

export const selectMenuBookEditorSelectedItemIdMap = createSelector(
  selectMenuBookEditorSelectedItemIds,
  (selectedItemIds) => {
    return selectedItemIds.reduce((previous: { [id: number]: true }, current: number): { [id: number]: true } => {
      previous[current] = true;
      return previous;
    }, {});
  }
);

// Reducer
export const menuBookEditorReducer = menuBookEditorSlice.reducer;
